import { useState, useCallback } from 'react';
import { UseColumnReorderReturn, EnhancedTableColumn } from '../../types';

/**
 * Hook for managing table column reordering functionality
 *
 * This hook provides functionality to reorder table columns via drag and drop
 * or programmatic changes, maintaining the column order state.
 *
 * @param initialOrder - Initial order of column IDs
 * @param onColumnReorder - Optional callback when columns are reordered
 *
 * @returns Object containing:
 *   - columnOrder: Current order of column IDs
 *   - handleColumnReorder: Function to reorder columns by index
 *   - getOrderedColumns: Function to get columns in the current order
 *
 * @example
 * ```tsx
 * const { columnOrder, handleColumnReorder, getOrderedColumns } = useColumnReorder(
 *   ['name', 'email', 'status'],
 *   (fromIndex, toIndex) => console.log(`Moved column from ${fromIndex} to ${toIndex}`)
 * );
 *
 * // Reorder columns (move first column to second position)
 * handleColumnReorder(0, 1);
 *
 * // Get columns in the current order
 * const orderedColumns = getOrderedColumns(columns);
 * ```
 */
export const useColumnReorder = (
  initialOrder?: string[],
  onColumnReorder?: (fromIndex: number, toIndex: number) => void,
): UseColumnReorderReturn => {
  const [columnOrder, setColumnOrder] = useState<string[]>(initialOrder || []);

  const handleColumnReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      const newOrder = [...columnOrder];
      const [movedColumn] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedColumn);

      setColumnOrder(newOrder);
      onColumnReorder?.(fromIndex, toIndex);
    },
    [columnOrder, onColumnReorder],
  );

  const getOrderedColumns = useCallback(
    <T>(columns: EnhancedTableColumn<T>[]): EnhancedTableColumn<T>[] => {
      if (columnOrder.length === 0) {
        // Initialize order based on column IDs
        const initialOrder = columns.map((col) => String(col.id));
        setColumnOrder(initialOrder);
        return columns;
      }

      // Create a map for faster lookups
      const columnMap = new Map(columns.map((col) => [String(col.id), col]));

      // Return columns in the specified order
      const orderedColumns: EnhancedTableColumn<T>[] = [];
      const unorderedColumns: EnhancedTableColumn<T>[] = [];

      columnOrder.forEach((columnId) => {
        const column = columnMap.get(columnId);
        if (column) {
          orderedColumns.push(column);
          columnMap.delete(columnId);
        }
      });

      // Add any columns not in the order to the end
      columnMap.forEach((column) => {
        unorderedColumns.push(column);
      });

      return [...orderedColumns, ...unorderedColumns];
    },
    [columnOrder],
  );

  return {
    columnOrder,
    handleColumnReorder,
    getOrderedColumns,
  };
};
